import type { LogMetadata } from "@indeed/signals-logger";
import { useMemo } from "react";

import type { TareBridge } from "../internal/NativeModulesTypes";
import { useTare } from "../internal/useTare";

export type ElementType = "ALERT" | "BANNER" | "TOAST" | "SUB_TAB";
export type EventLogExtraParamValue = string | number | boolean;
export type EventLogExtraParams = Record<string, EventLogExtraParamValue>;
export type JSTLogMetadata = LogMetadata;

export interface JSTEventLogApi {
  logScreenView(screenName: string): Promise<void>;
  logScreenViewWithExtraParams(
    screenName: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void>;
  logElementView(
    screenName: string,
    elementName: string,
    elementType: ElementType,
    tags?: string[],
    extraParams?: EventLogExtraParams,
  ): Promise<void>;
  logTapButton(screenName: string, elementName: string): Promise<void>;
  logTapButtonWithExtraParams(
    screenName: string,
    elementName: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void>;
  logLegacyGenericEvent(
    eventName: string,
    eventType: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void>;
  logNonFatal(
    message: string,
    errCode: number,
    errDomain: string,
    otherUserInfo: Record<string, string>,
  ): Promise<void>;
  logRNBridgeInternal(event: object, metadata?: JSTLogMetadata): Promise<void>;
}

export const createJSTEventLog = (getBridge: () => TareBridge | undefined): JSTEventLogApi => ({
  async logScreenView(screenName: string): Promise<void> {
    const bridge = getBridge();
    await bridge?.logScreenView?.(screenName);
  },
  async logScreenViewWithExtraParams(
    screenName: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void> {
    const bridge = getBridge();
    await bridge?.logScreenViewWithExtraParams?.(screenName, tags, extraParams);
  },
  async logElementView(
    screenName: string,
    elementName: string,
    elementType: ElementType,
    tags: string[] = [],
    extraParams: EventLogExtraParams = {},
  ): Promise<void> {
    const bridge = getBridge();
    await bridge?.logElementView?.(screenName, elementName, elementType, tags, extraParams);
  },
  async logTapButton(screenName: string, elementName: string): Promise<void> {
    const bridge = getBridge();
    await bridge?.logTapButton?.(screenName, elementName);
  },
  async logTapButtonWithExtraParams(
    screenName: string,
    elementName: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void> {
    const bridge = getBridge();
    await bridge?.logTapButtonWithExtraParams?.(screenName, elementName, tags, extraParams);
  },
  /** @deprecated Use only for events that do not fit the standard event patterns. */
  async logLegacyGenericEvent(
    eventName: string,
    eventType: string,
    tags: string[],
    extraParams: EventLogExtraParams,
  ): Promise<void> {
    const bridge = getBridge();
    await bridge?.logLegacyGenericEvent?.(eventName, eventType, tags, extraParams);
  },
  async logNonFatal(
    message: string,
    errCode: number,
    errDomain: string,
    otherUserInfo: Record<string, string>,
  ): Promise<void> {
    const bridge = getBridge();
    await bridge?.logNonFatal?.(message, errCode, errDomain, otherUserInfo);
  },
  async logRNBridgeInternal(event: object, metadata?: JSTLogMetadata): Promise<void> {
    const bridge = getBridge();
    await bridge?.logRNBridgeInternal?.(event, metadata);
  },
});

export function useJSTEventLog(): JSTEventLogApi {
  const tareBridge = useTare();
  return useMemo(() => createJSTEventLog(() => tareBridge), [tareBridge]);
}

export default useJSTEventLog;
